import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import EmployeeDetailModal from './EmployeeDetailModal';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const getInitials = (employee) =>
  `${employee.firstName?.charAt(0) || ''}${employee.lastName?.charAt(0) || ''}`.toUpperCase();

const getAvatarUrl = (employee) => {
  if (employee.avatar?.filePath) {
    return `${apiUrl}/${employee.avatar.filePath.replace(/\\/g, '/')}`;
  }

  return null;
};

const formatEmployeeName = (employee) => `${employee.firstName} ${employee.lastName}`;

const DirectoryPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('arama') || '');
  const [selectedDepartment, setSelectedDepartment] = useState(() => searchParams.get('departman') || '');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [directoryData, setDirectoryData] = useState({ employees: [], departments: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDirectory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await api.getDirectoryUsers({
        search: searchQuery,
        department: selectedDepartment
      });

      setDirectoryData({
        employees: response.employees || [],
        departments: response.departments || []
      });
    } catch (fetchError) {
      console.error('Directory fetch error:', fetchError);
      setError('Personel rehberi yüklenirken bir sorun oluştu.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, selectedDepartment]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDirectory();
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [fetchDirectory]);

  const totalDirectoryCount = useMemo(
    () => directoryData.departments.reduce((total, item) => total + item.count, 0),
    [directoryData.departments]
  );

  // "Seçili ekip üyesi": bir departman seçiliyse o departmanın toplam (şirket
  // genelindeki) çalışan sayısını, hiçbir departman seçili değilse tüm rehberin
  // toplamını gösterir — yani `totalDirectoryCount` ile aynı değeri taşır.
  // Backend'deki departman sayıları (`directoryData.departments`) arama
  // kutusundaki metne göre filtrelenmez (bkz. userController.getDirectoryUsers),
  // bu yüzden bu sayı arama sonucunda listelenen çalışan sayısıyla (
  // `directoryData.employees.length`) birebir eşleşmeyebilir; her zaman
  // "seçili departmandaki toplam kişi sayısı"nı temsil eder.
  const selectedDepartmentCount = useMemo(() => {
    if (!selectedDepartment) {
      return totalDirectoryCount;
    }

    return directoryData.departments.find((item) => item.name === selectedDepartment)?.count || 0;
  }, [directoryData.departments, selectedDepartment, totalDirectoryCount]);

  return (
    <div className="documents-page directory-experience">
      <div className="container-fluid">
        <div className="modern-card p-4 corporate-documents-hero mb-4">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <h3 className="fw-bold text-dark mb-1">Çalışan Rehberi</h3>
              <p className="text-muted mb-0">Fotoğraflı ekip rehberi ve departman görünümü</p>
            </div>
            <div className="directory-hero-stats">
              <div className="directory-stat-card">
                <strong>{totalDirectoryCount}</strong>
                <span>Rehberde görünür çalışan</span>
              </div>
              <div className="directory-stat-card">
                <strong>{directoryData.departments.length}</strong>
                <span>Aktif departman</span>
              </div>
              <div
                className="directory-stat-card"
                title="Aşağıdan bir departman seçtiğinizde o departmanın toplam çalışan sayısını gösterir; departman seçilmediğinde rehberdeki toplam çalışan sayısıyla aynıdır."
              >
                <strong>{selectedDepartmentCount}</strong>
                <span>Seçili ekip üyesi</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-card p-4 mb-4">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-search-alt"></i>
              <h4>Arama ve filtreler</h4>
            </div>
          </div>

          <div className="modern-hr-filters">
            <div className="row g-3 align-items-end">
              <div className="col-lg-7">
                <label className="modern-hr-form-label">Ara</label>
                <div className="modern-hr-search-input">
                  <i className="bx bx-search"></i>
                  <input
                    type="text"
                    className="modern-hr-form-input"
                    placeholder="İsim, unvan, departman, lokasyon veya dahili numara"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
              </div>
              <div className="col-lg-3">
                <label className="modern-hr-form-label">Departman</label>
                <select
                  className="modern-hr-form-select"
                  value={selectedDepartment}
                  onChange={(event) => setSelectedDepartment(event.target.value)}
                >
                  <option value="">Tüm departmanlar</option>
                  {directoryData.departments.map((department) => (
                    <option key={department.name} value={department.name}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-2">
                <button
                  type="button"
                  className="modern-hr-btn modern-hr-btn-secondary w-100"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedDepartment('');
                  }}
                >
                  Filtreyi temizle
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="modern-card p-4 mb-4">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-sitemap"></i>
              <h4>Departmanlar</h4>
            </div>
            <p className="modern-hr-widget-subtitle">
              Departman adına tıklayarak o ekibi rehberde filtreleyin.
            </p>
          </div>

          <div className="directory-department-grid">
            <button
              type="button"
              className={`directory-department-card ${selectedDepartment === '' ? 'active' : ''}`}
              onClick={() => setSelectedDepartment('')}
            >
              <strong>Tüm ekipler</strong>
              <span>{totalDirectoryCount} kişi</span>
            </button>
            {directoryData.departments.map((department) => (
              <button
                key={department.name}
                type="button"
                className={`directory-department-card ${
                  selectedDepartment === department.name ? 'active' : ''
                }`}
                onClick={() => setSelectedDepartment(department.name)}
              >
                <strong>{department.name}</strong>
                <span>{department.count} kişi</span>
              </button>
            ))}
          </div>
        </div>

        <div className="modern-card p-4">
          <div className="modern-hr-widget-header">
            <div className="modern-hr-widget-title">
              <i className="bx bx-group"></i>
              <h4>
                {selectedDepartment ? `${selectedDepartment} ekibi` : 'Tüm çalışanlar'}
              </h4>
            </div>
            <span className="modern-hr-count">
              {directoryData.employees.length} çalışan listeleniyor
            </span>
          </div>

          {loading ? (
            <div className="modern-hr-loading">
              <i className="bx bx-loader-alt bx-spin"></i>
              <p>Rehber yükleniyor...</p>
            </div>
          ) : error ? (
            <div className="modern-hr-empty">
              <i className="bx bx-error-circle"></i>
              <h5>Rehber verisi alınamadı</h5>
              <p>{error}</p>
            </div>
          ) : directoryData.employees.length === 0 ? (
            <div className="modern-hr-empty">
              <i className="bx bx-user-x"></i>
              <h5>Eşleşen çalışan bulunamadı</h5>
              <p>Arama teriminizi veya departman seçiminizi güncelleyip tekrar deneyin.</p>
            </div>
          ) : (
            <div className="directory-card-grid">
              {directoryData.employees.map((employee) => {
                const avatarUrl = getAvatarUrl(employee);

                return (
                  <div key={employee._id} className="directory-employee-card">
                    <div className="directory-employee-header">
                      <div className="directory-employee-avatar">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={formatEmployeeName(employee)} />
                        ) : (
                          <div className="directory-employee-avatar-fallback">
                            {getInitials(employee)}
                          </div>
                        )}
                      </div>
                      <div>
                        <h5>{formatEmployeeName(employee)}</h5>
                        <p>{employee.position}</p>
                        <button
                          type="button"
                          className="directory-department-link"
                          onClick={() => setSelectedDepartment(employee.department)}
                        >
                          {employee.department}
                        </button>
                      </div>
                    </div>

                    <div className="directory-employee-meta">
                      <div>
                        <i className="bx bx-envelope"></i>
                        <span>{employee.email}</span>
                      </div>
                      {employee.phone && (
                        <div>
                          <i className="bx bx-phone"></i>
                          <span>{employee.phone}</span>
                        </div>
                      )}
                      {(employee.extension || employee.officeLocation) && (
                        <div>
                          <i className="bx bx-building-house"></i>
                          <span>
                            {[employee.officeLocation, employee.extension && `Dahili ${employee.extension}`]
                              .filter(Boolean)
                              .join(' • ')}
                          </span>
                        </div>
                      )}
                      {employee.company && (
                        <div>
                          <i className="bx bxs-buildings"></i>
                          <span>{employee.company}</span>
                        </div>
                      )}
                    </div>

                    {employee.bio && <p className="directory-employee-bio">{employee.bio}</p>}

                    <div className="directory-employee-actions">
                      <button
                        type="button"
                        className="modern-hr-btn modern-hr-btn-primary"
                        onClick={() => setSelectedEmployee(employee)}
                      >
                        Profili aç
                      </button>
                      <a
                        className="modern-hr-btn modern-hr-btn-secondary"
                        href={`mailto:${employee.email}`}
                      >
                        E-posta
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <EmployeeDetailModal
        employee={selectedEmployee}
        isOpen={Boolean(selectedEmployee)}
        onClose={() => setSelectedEmployee(null)}
      />
    </div>
  );
};

export default DirectoryPage;
